import { Component, EventEmitter, Input, Output, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { ViewScopeService } from '../../../../../services/view-scope.service';
import { AjaxService } from '../../../../../services/ajax.service';
import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';


// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';

import { LST_OF_COUNTRIES, LST_OF_ADDR_TYPE, LST_OF_CITY_IN_COUNTRY, LST_OF_STREETS_IN_CITY} from '../../../../../services/account-api.constants';

// Interface for an individual address entry
export interface Address {
    id: string;
    addrRecId: string;
    addressTypeRecId: string | null;
    addrType: SelectItem | null;
    streetRecID: string | null;
    street: SelectItem | null;
    cityRecId: string | null;
    city: SelectItem | null;
    countyRecId: string | null;
    country: SelectItem | null;
    appartment: string | null;
    postalCode: string | null;
    postalBox: string | null;
    streetName: string | null;
    entrence: string | null;
    houseNumber: string;
    isActive: number; // 0 / 1.
    isApproved: number; //  0 / 1.
    description: string;
}

// Interface for dropdown options (re-using SelectItem from previous components)
interface SelectItem {
    label: string;
    code?: number;
    value: string;
}

@Component({
    selector: 'app-addresses',
    standalone: true,
    imports: [
        CommonModule,
        TagModule,
        FormsModule,
        InputTextModule,
        InputTextarea,
        ButtonModule,
        ToastModule,
        TableModule,
        DropdownModule,
        AutoCompleteModule
    ],
    templateUrl: './addresses.component.html',
    styleUrls: ['./addresses.component.scss'],
    providers: [MessageService]
})
export class AddressesComponent implements OnInit {
    @Input() addresses: Address[] = [];
    @Output() addressesChange = new EventEmitter<Address[]>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();

	@Output() skipToLastStep = new EventEmitter<void>();

    @Input() customerRecId!: number;


    readonly DESCRIPTION_MAX_LENGTH = 250;
    readonly POSTAL_CODE_LENGTH = 7; // Assuming a 7-digit postal code

    loading: boolean = true;

    isDialogMode = false;
    isCreate = false;
    newEntry: Address = {
        id: '',
        addrRecId: '',
        addressTypeRecId: '',
        addrType: null,
        streetRecID: '',
        street: null,
        cityRecId: '',
        city: null,
        countyRecId: '',
        country: null,
        appartment: '',
        postalCode: '',
        postalBox: '',
        streetName: '',
        houseNumber: '',
        entrence: '',
        isActive: 1,
        isApproved: 1,
        description: ''
    };

    // Dropdown options
    addressTypeOptions: SelectItem[] = [];
    countryOptions: SelectItem[] = [];
    cityOptions: SelectItem[] = [];
    streetNameOptions: SelectItem[] = [];

    // Filtered arrays for autocomplete
    filteredAddressTypes: any[] = [];
    filteredCountries: any[] = [];
    filteredCities: any[] = [];
    filteredStreetNames: any[] = [];

    constructor(private messageService: MessageService,
        public translateServ: TranslateService,
        public dirServ: DirectionService,
        private ajaxService: AjaxService,
        private viewScopeService: ViewScopeService,
        @Optional() public ref: DynamicDialogRef,
        @Optional() public config: DynamicDialogConfig,
        @Optional() private maintainAccountService: MaintainAccountService) { }

    ngOnInit(): void {
        this.loading = true; // start loading

        if (this.config && this.config.data) {
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;

            if (!this.isCreate && this.config.data.nameEntry) {
                this.addresses = [this.config.data.nameEntry]; // edit mode
            } else {
                this.addresses = [];
            }
        } else {
            this.isDialogMode = false;
            this.isCreate = true;
            this.resetNewEntry();
        }

        // Prepare promises for dropdowns and dependent data
        const promises: Promise<any>[] = [
            this.loadAddrTypes(),
            this.loadCountries()
        ];

        // Load cities/streets for all addresses that have RecIds (for edit or restored session)
        this.addresses.forEach(entry => {
            if (entry.countyRecId) {
                promises.push(this.loadCites(entry));
            }
        });

        Promise.all(promises)
            .then(() => {
                // Always map dropdown objects for all addresses after lookups are ready
                this.addresses.forEach(entry => {
                    entry.addrType = this.addressTypeOptions.find(a => a.code?.toString() === entry.addressTypeRecId?.toString()) || null;
                    entry.country = this.countryOptions.find(c => c.code?.toString() === entry.countyRecId?.toString()) || null;
                    entry.city = this.cityOptions?.find(c => c.code?.toString() === entry.cityRecId?.toString()) || null;
                    entry.street = this.streetNameOptions?.find(c => c.code?.toString() === entry.streetRecID?.toString()) || null;
                });
            })
            .finally(() => {
                this.loading = false; // stop loading
            });
    }


    private generateUniqueId(): string {
        return 'id_' + Math.random().toString(36).substring(2, 9);
    }

    resetNewEntry(): void {
        this.newEntry = {
            id: '',
            addrRecId: '',
            addressTypeRecId: '',
            addrType: null,
            streetRecID: '',
            street: null,
            cityRecId: '',
            city: null,
            countyRecId: '',
            country: null,
            appartment: '',
            postalCode: '',
            postalBox: '',
            streetName: '',
            houseNumber: '',
            entrence: '',
            isActive: 1,
            isApproved: 1,
            description: ''
        };
    }

    addEntry(): void {
        if (!this.newEntry.addressTypeRecId || !this.newEntry.country || !this.newEntry.city) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Address Type, Country, City, and Street Name are required for new entry.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.postalCode && !/^\d{7}$/.test(this.newEntry.postalCode)) { // Validate 7-digit postal code
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Postal Code must be a 7-digit number.`,
                life: 3000
            });
            return;
        }
        // No strict validation for houseNumber as it can be any string
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }

        this.addresses.push({ ...this.newEntry });
        this.emitChanges();
        if (this.isCreate) {
            this.resetNewEntry();
        }

        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New address added.',
            life: 1500
        });
    }

    removeEntry(entryToRemove: Address): void {
        this.addresses = this.addresses.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'Address removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.addressesChange.emit(this.addresses);
    }

    getRemainingChars(description: string | null | undefined): number {
        return this.DESCRIPTION_MAX_LENGTH - (description ? description.length : 0);
    }



    validateAndProceed(): void {
        let isValid = true;
        for (const entry of this.addresses) {
            if (!entry.addressTypeRecId || !(entry.country && entry.country.code) || !(entry.city && entry.city.code) || !(entry.street && entry.street.code)) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Address Type, Country, City, and Street Name are required for all entries.`,
                    life: 3000
                });
                break;
            }
            if (entry.postalCode && !/^\d{7}$/.test(entry.postalCode)) { // Validate 7-digit postal code
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Postal Code for entry "${entry.id}" must be a 7-digit number.`,
                    life: 3000
                });
                break;
            }
            if (entry.description?.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for entry "${entry.streetName || (entry.city && entry.city.label) || 'this entry'}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

    /**
     * Helper function to filter options for autocomplete.
     * If the query is empty, it returns all available options.
     * Otherwise, it filters based on the query and adds the query as a custom option if no exact match is found.
     * @param options The full list of SelectItem options.
     * @param query The current input query string.
     * @returns A filtered array of SelectItem options.
     */
    searchWithCustomValue(options: SelectItem[], query: string): SelectItem[] {
        // If the query is empty or just whitespace, return all available options.
        // This ensures the dropdown opens with all items when focused or dropdown button is clicked.
        if (!query || query.trim() === '') {
            return [...options]; // Return a shallow copy to ensure change detection
        }

        const lowerCaseQuery = query.toLowerCase().trim();
        const filtered = options.filter(option =>
            option.label.toLowerCase().includes(lowerCaseQuery)
        );

        // Check if the query exactly matches any existing option
        const exactMatch = options.find(option =>
            option.label.toLowerCase() === lowerCaseQuery
        );

        // If no exact match and query is not empty, add as custom option
        if (!exactMatch) {
            filtered.push({
                label: query, // Use original query for label
                value: query, // Use original query for value
            });
        }
        return filtered;
    }

    searchAddressTypes(event: any) {
        this.filteredAddressTypes = this.searchWithCustomValue(this.addressTypeOptions, event.query);
    }

    searchCountries(event: any) {
        this.filteredCountries = this.searchWithCustomValue(this.countryOptions, event.query);
    }

    searchCities(event: any) {
        this.filteredCities = this.searchWithCustomValue(this.cityOptions, event.query);
    }

    searchStreetNames(event: any) {
        this.filteredStreetNames = this.searchWithCustomValue(this.streetNameOptions, event.query);
    }

    applyRowChanges(entry: Address): void {
        // Simply emit the changes so parent knows data is dirty
        this.emitChanges();

        this.messageService.add({
            severity: 'info',
            summary: 'Changes Applied',
            detail: `Changes applied for "${entry.id}". Remember to press Save to persist.`,
            life: 2500
        });
    }


    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }

    /**
     * This function is intended for the dialog's "save" button.
     * It validates the form and then calls the API to save/update the data.
     * It uses forkJoin to wait for all API calls to complete before closing the dialog.
     */
    saveAndSubmit(): void {
        // First, run validation
        let isValid = true;

        for (const entry of this.addresses) {
            if (!entry.country && !entry.city) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `County and city are required for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if (entry.description?.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.country} - ${entry.city} - ${entry.streetName}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        // Only attempt API calls if the service is available
        if (this.maintainAccountService) {
            const requests = this.addresses.map(entry => {
                const basePayload: any = {
                    // AddressRecID: entry.addrType?.code,
                    StreetRecID: entry.streetRecID,
                    CityRecID: entry.city?.code,
                    CountryRecID: entry.country?.code,
                    AddressTypeRecID: entry.addrType?.code,
                    PostalCode: entry.postalCode,
                    House: entry.houseNumber,
                    Appartment: entry.appartment,
                    Entrence: entry.entrence,
                    PostalBox: entry.postalBox,
                    Descr: entry.description || '',   // safe for empty
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId()!
                };

                if (this.isCreate) {
                    // For create → use CustomerRecID
                    basePayload.CustomerSupplierRecID = this.customerRecId;
                } else {
                    // For edit → use RecID (taken from the entry provided by tab screen)
                    basePayload.RecID = entry.id;
                    basePayload.AddressRecID = entry.addrRecId;
                }

                return basePayload;
            });


            console.log("Data we will save:", requests);

            // Call API for each record separately
            requests.forEach(record => {
                this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record)).subscribe(
                    {
                        next: (response) => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Saved record for "${record.AdditName}".`,
                                life: 2000
                            });

                            // Close dialog only after the last record
                            if (this.ref && record === requests[requests.length - 1]) {
                                this.ref.close(response);
                            }
                        },
                        error: (error) => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: `Failed to save record for "${record.AdditName}".`,
                                life: 3000
                            });
                        },
                        complete: () => {
                            // This block runs after all API calls are complete
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Complete',
                                detail: 'All records processed.',
                                life: 2000
                            });
                            // Reload data from API after successful submission

                            //this.loadDataFromApi();


                            // Close dialog
                            if (this.ref) {
                                this.ref.close(true);
                            }
                        }
                    });
            });

        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'This functionality is not available in standalone mode.',
                life: 3000
            });
        }
    }

    // When country is selected
    onCountryChange(entry: Address, country: any | null): void {
        entry.country = country;
        entry.city = null;
        entry.cityRecId = null;
        entry.streetName = null;
        entry.streetRecID = null;
        this.cityOptions = [];
        this.streetNameOptions = [];

        if (country && country.code) {
            entry.countyRecId = country.code.toString();
            this.loadCites(entry);
        }

        this.emitChanges();
    }


    // When city is selected
    onCityChange(entry: Address, city: SelectItem | null): void {
        entry.city = city;
        entry.streetName = null;
        entry.streetRecID = null;
        this.streetNameOptions = [];

        if (city && city.code) {
            entry.cityRecId = city.code.toString();
            this.loadStreet(entry);
        }

        this.emitChanges();
    }

    onStreetChange(entry: Address, street: SelectItem | null): void {
        entry.street = street;

        if (street && street.code) {
            entry.streetRecID = street.code.toString();
        }

        this.emitChanges();
    }


    loadCountries(): Promise<void> {
        return new Promise((resolve, reject) => {
            const url = this.ajaxService.getBaseOfApi() + LST_OF_COUNTRIES + this.viewScopeService.getCurrentOpertaingUserId();;

            if (!url || !this.maintainAccountService) {
                console.warn('API endpoint or service missing.');
                resolve(); // still resolve so Promise.all doesn't hang
                return;
            }

            this.ajaxService.getData(url).subscribe({
                next: (response) => {
                    const data = response as any[];
                    this.countryOptions = data.map(item => ({
                        label: item.Name,
                        code: item.CountryRecID,
                        value: item.Name
                    }));
                    this.filteredCountries = [...this.countryOptions];
                    resolve();
                },
                error: (error) => {
                    console.error('Failed to load countries', error);
                    this.countryOptions = [];
                    this.filteredCountries = [];
                    resolve(); // resolve even on error to continue
                }
            });
        });
    }


    loadAddrTypes(): Promise<void> {
        return new Promise((resolve) => {
            const url = this.ajaxService.getBaseOfApi() + LST_OF_ADDR_TYPE + this.viewScopeService.getCurrentOpertaingUserId();
            if (!url || !this.maintainAccountService) {
                console.warn('API endpoint or service missing.');
                resolve();
                return;
            }

            this.ajaxService.getData(url).subscribe({
                next: (response) => {
                    const data = response as any[];
                    this.addressTypeOptions = data.map(item => ({
                        label: item.AddressType,
                        code: item.AddressTypeRecID,
                        value: item.AddressType
                    }));
                    this.filteredAddressTypes = [...this.addressTypeOptions];
                    resolve();
                },
                error: (error) => {
                    console.error('Failed to load address types', error);
                    this.addressTypeOptions = [];
                    this.filteredAddressTypes = [];
                    resolve();
                }
            });
        });
    }

    loadCites(entry: Address): Promise<void> {
        return new Promise((resolve) => {
            if (!entry.countyRecId) { resolve(); return; }

            const url = this.ajaxService.getBaseOfApi() + LST_OF_CITY_IN_COUNTRY + entry.countyRecId + "/" + this.viewScopeService.getCurrentOpertaingUserId();

            this.ajaxService.getData(url).subscribe({
                next: (response) => {
                    const data = response as any[];
                    this.cityOptions = data.map(item => ({
                        label: item.Name,
                        code: item.Code,
                        value: item.Name
                    }));
                    this.filteredCities = [...this.cityOptions];

                    const match = this.cityOptions.find(c => c.code?.toString() === entry.cityRecId?.toString());
                    if (match) {
                        entry.city = match;
                        this.loadStreet(entry).then(() => resolve());
                    } else {
                        entry.city = null;
                        resolve();
                    }
                },
                error: () => {
                    this.cityOptions = [];
                    this.filteredCities = [];
                    resolve();
                }
            });
        });
    }

    loadStreet(entry: Address): Promise<void> {
        return new Promise((resolve) => {
            if (!entry.cityRecId) { resolve(); return; }

            const url = this.ajaxService.getBaseOfApi() + LST_OF_STREETS_IN_CITY + entry.cityRecId + "/" + this.viewScopeService.getCurrentOpertaingUserId();

            this.ajaxService.getData(url).subscribe({
                next: (response) => {
                    const data = response as any[];
                    this.streetNameOptions = data.map(item => ({
                        label: item.Name,
                        code: item.StreetRecID,
                        value: item.Name
                    }));
                    this.filteredStreetNames = [...this.streetNameOptions];

                    if (entry.streetRecID) {
                        const match = this.streetNameOptions.find(c => c.code?.toString() === entry.streetRecID?.toString());
                        entry.street = match || null;
                    }
                    resolve();
                },
                error: () => {
                    this.streetNameOptions = [];
                    this.filteredStreetNames = [];
                    resolve();
                }
            });
        });
    }


    toggleIsActive(entry: any): void {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }

    // When address type is selected
    onAddressTypeChange(entry: Address, addrType: SelectItem | null): void {
        entry.addrType = addrType;
        entry.addressTypeRecId = addrType && addrType.code ? addrType.code.toString() : null;

        this.emitChanges();
    }

}
